import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import type { ChatStoredMessage } from '@/api/types';
import { MessageBubble } from './MessageBubble';

interface Props {
  messages: ChatStoredMessage[];
  renderFooter?: (msg: ChatStoredMessage) => React.ReactNode;
  autoScroll?: boolean;
  className?: string;
}

export function MessageTimeline({
  messages,
  renderFooter,
  autoScroll,
  className,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const endRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (autoScroll) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [autoScroll, messages.length]);

  if (messages.length === 0) {
    return (
      <div className={className}>
        <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <MessageSquare className="h-6 w-6" />
          <span>{t('chat.messages.empty')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {messages.map((m) => (
          <MessageBubble key={m.messageId} message={m} footer={renderFooter?.(m)} />
        ))}
        <div ref={endRef} />
      </div>
    </div>
  );
}
